import { FC, useState } from 'react';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
} from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Order } from '@libs/constants';
import { Trans } from '@libs/i18n';
import { AssessmentsSortByType } from '@libs/types';
import { FunnelIcon } from '@phosphor-icons/react';

interface AssessmentSortingProps {
  sortBy: AssessmentsSortByType;
  order: Order;
  onSortChange: (sortBy: AssessmentsSortByType, order: Order) => void;
}

export const AssessmentSorting: FC<AssessmentSortingProps> = observer(
  ({ sortBy, order, onSortChange }) => {
    const theme = useTheme();
    const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);

    const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
      setSortAnchorEl(event.currentTarget);
    };

    const handleSortClose = () => {
      setSortAnchorEl(null);
    };

    const handleSortSelect = (newSortBy: AssessmentsSortByType, newOrder: Order) => {
      onSortChange(newSortBy, newOrder);
      handleSortClose();
    };

    return (
      <Box sx={{ width: 'fit-content', display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={handleSortClick}
          sx={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            gap: 1,
            borderRadius: 2,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <FunnelIcon size={20} color={theme.palette.text.primary} />
          <Typography variant="body2">
            <Trans i18nKey="app.assessments.filters.label" />
          </Typography>
        </IconButton>

        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={handleSortClose}
          slotProps={{
            paper: {
              sx: {
                minWidth: 200,
                borderRadius: 2,
                mt: 1,
                border: 'none',
                boxShadow: theme.shadows[8],
              },
            },
          }}
        >
          <MenuItem
            onClick={() => handleSortSelect('title', Order.ASC)}
            selected={sortBy === 'title' && order === Order.ASC}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ArrowUpwardIcon fontSize="small" />
              <Trans i18nKey="app.assessments.sort.nameAsc" />
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('title', Order.DESC)}
            selected={sortBy === 'title' && order === Order.DESC}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ArrowDownwardIcon fontSize="small" />
              <Trans i18nKey="app.assessments.sort.nameDesc" />
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('updatedAt', Order.ASC)}
            selected={sortBy === 'updatedAt' && order === Order.ASC}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ArrowUpwardIcon fontSize="small" />
              <Trans i18nKey="app.assessments.sort.updatedAsc" />
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('updatedAt', Order.DESC)}
            selected={sortBy === 'updatedAt' && order === Order.DESC}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ArrowDownwardIcon fontSize="small" />
              <Trans i18nKey="app.assessments.sort.updatedDesc" />
            </Box>
          </MenuItem>
        </Menu>
      </Box>
    );
  },
);
